import { Col, Empty, Input, Row, Space, Table } from "antd"
import { useState } from "react";
import { useQuery } from "react-query";
import { useDebouncedState } from "../../hooks/useDebouncedState";
import { getCustomerPage } from "./propertySelectorModel";

export type PropertySelection = { customerId: number, propertyId: number } | undefined
export type Props = {
  onChange: (data: PropertySelection) => void
}

const PropertySelector = ({ onChange }: Props) => {
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | undefined>(undefined)
  const [selectedPropertyId, setSelectedPropertyId] = useState<number | undefined>(undefined)

  const [page, setPage] = useState(1)
  const [filter, setFilter] = useState('')
  const pageSize = 10
  const { debouncedState: debouncedFilter, setState: setDebouncedFilter } = useDebouncedState('', 250, () => {
    setPage(1)
    setSelection(undefined, undefined)
  })

  const customers = useQuery(['customers', { tablePage: page, pageSize, debouncedFilter }], () => getCustomerPage(page, pageSize, debouncedFilter))

  const setSelection = (customerId?: number, propertyId?: number) => {
    setSelectedCustomerId(customerId)
    setSelectedPropertyId(propertyId)

    if (customerId !== undefined && propertyId !== undefined) {
      onChange({ customerId, propertyId })
      return
    }

    if (customerId !== undefined && propertyId === undefined) {
      const properties = customers.data?.customers.find(x => x.customerId === customerId)?.properties
      if (properties?.length === 1) {
        setSelectedPropertyId(properties[0].propertyId)
        onChange({ customerId, propertyId: properties[0].propertyId })
        return
      }
    }

    onChange(undefined)
  }

  return <Space direction="vertical" style={{ width: '100%' }}>
    <Input.Search style={{ maxWidth: '400px' }} placeholder="Search for customer or property" value={filter} onChange={(e) => {
      setFilter(e.target.value)
      setDebouncedFilter(e.target.value)
    }} />
    <Row gutter={8}>
      <Col span={12}>
        <Table
          style={{ flex: 1 }}
          size="small"
          loading={customers.isLoading}
          dataSource={customers.data?.customers}
          rowSelection={{
            type: 'radio',
            selectedRowKeys: selectedCustomerId !== undefined ? [selectedCustomerId] : undefined,
            onChange: x => setSelection(x[0] as number, undefined)
          }}
          columns={[
            {
              title: 'Customer',
              key: 'customer',
              render: (item) => item.displayName,
              onCell: () => {
                return {
                  style: { cursor: 'pointer' }
                }
              }
            }
          ]}
          rowKey="customerId"
          onRow={(record) => {
            return {
              onClick: () => setSelection(record.customerId, undefined)
            };
          }}
          pagination={{ current: page, pageSize, total: customers.data?.total, showSizeChanger: false }}
          onChange={(pagination) => setPage(pagination.current ?? 1)}
        />
      </Col>
      <Col span={12}>
        <Table
          style={{ flex: 1 }}
          size="small"
          loading={customers.isLoading}
          dataSource={customers.data?.customers.find(x => x.customerId === selectedCustomerId)?.properties ?? []}
          rowSelection={{
            type: 'radio',
            selectedRowKeys: selectedPropertyId !== undefined ? [selectedPropertyId] : undefined,
            onChange: x => setSelection(selectedCustomerId, x[0] as number)
          }}
          columns={[
            {
              title: 'Property',
              key: 'property',
              render: (item) => item.displayName,
              onCell: () => {
                return {
                  style: { cursor: 'pointer' }
                }
              }
            }
          ]}
          rowKey="propertyId"
          onRow={(record) => {
            return {
              onClick: () => setSelection(selectedCustomerId, record.propertyId)
            };
          }}

          locale={{
            emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={selectedCustomerId === undefined ? 'Select a customer' : 'Customer has no properties'} />
          }}
          pagination={{ showSizeChanger: false }}
        />
      </Col>
    </Row>
  </Space>
}
export default PropertySelector
